import { Injectable } from '@angular/core';
import { PolylineType } from '@app/utilities/map/features/polyline';
import { geojson } from '@lib/geojson';
import { isFiniteNumber } from '@lib/number';
import { VehicleLocationQuery } from '@models';
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { LocationMapService } from './location-map.service';
import { LocationTelemetryService } from './location-telemetry.service';

type PlaybackVehicle = VehicleLocationQuery.Interval.VehicleLocationsWithPathColor;

type PlaybackMarker = {
	id: string;
	coordinate: geojson.Coord;
	name: string;
	color: string;
	playbackTime: string;
	speed: string;
	odometer: string;
};

@Injectable({
	providedIn: 'root',
})
export class LocationPlaybackService {
	public mode = false;
	public playing = false;
	public progress = 0;
	public speed = 1;
	public durationKey = '1hour';

	private beginTime: number | null = null;
	private endTime: number | null = null;
	private animationFrame: number | null = null;
	private startedAt = 0;
	private startProgress = 0;
	private vehicles: PlaybackVehicle[] = [];

	constructor(
		private readonly vehicleQuery: VehicleQueryService,
		private readonly locationMapService: LocationMapService,
		private readonly telemetry: LocationTelemetryService,
	) { }

	public setVehicles(vehicles: PlaybackVehicle[]): void {
		this.vehicles = vehicles;
	}

	public setDurationKey(durationKey: string): void {
		this.durationKey = durationKey;
	}

	public clearTimeBounds(): void {
		this.beginTime = null;
		this.endTime = null;
	}

	public updateTimeBounds(response: VehicleLocationQuery.Interval.Response | null): void {
		if (!response || this.mode) return;
		this.beginTime = Math.floor(response.begin.valueOf() / 1000);
		this.endTime = Math.floor(response.end.valueOf() / 1000);
	}

	public hasLocations(): boolean {
		return this.vehicles.length > 0;
	}

	public start(): void {
		if (!this.hasLocations()) return;

		this.vehicleQuery.setPlaybackActive(true);
		this.mode = true;
		this.progress = 0;
		this.locationMapService.addTracePaths([]);
		this.showPosition(true);
		this.play();
	}

	public toggle(): void {
		if (!this.mode) {
			this.start();
			return;
		}

		this.playing ? this.pause() : this.play();
	}

	public play(): void {
		if (!this.hasLocations()) return;
		if (this.progress >= 1) this.progress = 0;
		this.playing = true;
		this.startedAt = performance.now();
		this.startProgress = this.progress;
		this.animate();
	}

	public pause(): void {
		this.playing = false;
		this.cancelAnimation();
	}

	/** Refresh playback markers and paths after the visible vehicle set changes. */
	public refresh(): void {
		if (this.mode) this.showPosition();
	}

	public seek(progress: string): void {
		this.progress = Math.max(0, Math.min(1, Number(progress) / 100));
		this.startedAt = performance.now();
		this.startProgress = this.progress;
		if (this.mode) this.showPosition();
	}

	public setSpeed(speed: string | number): void {
		this.speed = Number(speed);
		if (this.playing) {
			this.startedAt = performance.now();
			this.startProgress = this.progress;
		}
	}

	public goLive(): void {
		this.pause();
		this.mode = false;
		this.vehicleQuery.setPlaybackActive(false);
		this.progress = 0;
		this.locationMapService.stopPlayback();
	}

	public formatTime(): string {
		const [first, last] = this.getTimeBounds();
		if (first === null || last === null) return 'No recorded location';
		return this.formatTimestamp(first + (last - first) * this.progress);
	}

	public updatePaths(targetTime?: number): void {
		if (!this.mode) return;
		const [begin, end] = this.getTimeBounds();
		if (begin === null || end === null) return;
		const currentTime = targetTime ?? begin + (end - begin) * this.progress;
		const tracePaths: PolylineType[] = this.vehicles
			.map((vehicle) => ({
				coordinates: this.getPathCoordinates(vehicle, currentTime),
				content: '',
				color: vehicle.color.toString(),
			}))
			.filter((path) => path.coordinates.length > 1);

		this.locationMapService.addTracePaths(tracePaths);
	}

	public destroy(): void {
		this.cancelAnimation();
		this.mode = false;
		this.playing = false;
		this.progress = 0;
		this.vehicleQuery.setPlaybackActive(false);
	}

	private animate = () => {
		if (!this.playing) return;
		const elapsed = performance.now() - this.startedAt;
		this.progress = Math.min(1, this.startProgress + (elapsed * this.speed / this.getDurationMs()));
		this.showPosition();
		if (this.progress < 1) {
			this.animationFrame = requestAnimationFrame(this.animate);
		} else {
			this.playing = false;
			this.animationFrame = null;
		}
	};

	private showPosition(start = false): void {
		const [firstTime, lastTime] = this.getTimeBounds();
		if (firstTime === null || lastTime === null) return;
		const targetTime = firstTime + (lastTime - firstTime) * this.progress;
		const playbackTime = this.telemetry.formatIdleEventTime(targetTime);
		const markers = this.vehicles.map((vehicle) => {
			const coordinate = this.getCoordinate(vehicle, targetTime);
			return coordinate ? {
				id: `${vehicle.vehicleId}`,
				coordinate,
				name: vehicle.vehicleName,
				color: vehicle.color.toString(),
				playbackTime,
				speed: this.telemetry.formatSpeed(this.getMetric(vehicle.locationSpeeds, vehicle.locationTimes, targetTime)),
				odometer: this.telemetry.formatOdometer(this.getMetric(vehicle.locationOdometers, vehicle.locationTimes, targetTime)),
			} : null;
		}).filter((marker): marker is PlaybackMarker => marker !== null);

		if (start) {
			this.locationMapService.startPlayback(markers);
		} else {
			this.locationMapService.updatePlaybackPosition(markers);
		}

		this.updatePaths(targetTime);
	}

	private getPathCoordinates(vehicle: PlaybackVehicle, targetTime: number): geojson.Coord[] {
		const coordinates = vehicle.vehicleLocations;
		const times = vehicle.locationTimes ?? [];
		if (coordinates.length < 2) return [];

		if (times.length === coordinates.length && times.length > 1) {
			if (targetTime < times[0]) return [];
			if (targetTime >= times[times.length - 1]) return [...coordinates];

			const nextIndex = times.findIndex((time) => time >= targetTime);
			if (nextIndex <= 0) return [];
			const currentCoordinate = this.getCoordinate(vehicle, targetTime);
			return currentCoordinate
				? [...coordinates.slice(0, nextIndex), currentCoordinate]
				: coordinates.slice(0, nextIndex);
		}

		const endIndex = Math.max(1, Math.floor(this.progress * (coordinates.length - 1)) + 1);
		return coordinates.slice(0, endIndex + 1);
	}

	private getCoordinate(vehicle: PlaybackVehicle, targetTime: number): geojson.Coord | null {
		const coordinates = vehicle.vehicleLocations;
		const times = vehicle.locationTimes ?? [];
		if (times.length === coordinates.length && times.length > 0) {
			if (targetTime < times[0]) return null;
			if (coordinates.length === 1 || targetTime >= times[times.length - 1]) {
				return coordinates[coordinates.length - 1];
			}

			const index = times.findIndex((time) => time >= targetTime);
			if (index <= 0) return coordinates[0];
			const ratio = (targetTime - times[index - 1]) / Math.max(1, times[index] - times[index - 1]);
			return [
				coordinates[index - 1][0] + (coordinates[index][0] - coordinates[index - 1][0]) * ratio,
				coordinates[index - 1][1] + (coordinates[index][1] - coordinates[index - 1][1]) * ratio,
			];
		}

		if (coordinates.length === 1) return coordinates[0];
		const position = this.progress * (coordinates.length - 1);
		const index = Math.floor(position);
		const ratio = position - index;
		const current = coordinates[index];
		const next = coordinates[Math.min(index + 1, coordinates.length - 1)];
		return [current[0] + (next[0] - current[0]) * ratio, current[1] + (next[1] - current[1]) * ratio];
	}

	private getMetric(values: (number | null)[] | undefined, times: number[], targetTime: number): number | null {
		if (!values?.length || values.length !== times.length) return null;
		if (targetTime <= times[0]) return values[0];
		if (targetTime >= times[times.length - 1]) return values[times.length - 1];

		const nextIndex = times.findIndex((time) => time >= targetTime);
		if (nextIndex <= 0) return values[0];
		const previous = values[nextIndex - 1];
		const next = values[nextIndex];
		if (!isFiniteNumber(previous) || !isFiniteNumber(next)) return next ?? previous ?? null;
		const ratio = (targetTime - times[nextIndex - 1]) / Math.max(1, times[nextIndex] - times[nextIndex - 1]);
		return previous + (next - previous) * ratio;
	}

	private getTimeBounds(): [number | null, number | null] {
		if (this.beginTime !== null && this.endTime !== null) {
			return [this.beginTime, this.endTime];
		}

		const durationSeconds = playbackDurationSeconds[this.durationKey] ?? 3600;
		const end = Math.floor(Date.now() / 1000);
		return [end - durationSeconds, end];
	}

	private getDurationMs(): number {
		const [begin, end] = this.getTimeBounds();
		return Math.max(1, (end - begin) * 1000);
	}

	private formatTimestamp(epochSeconds: number): string {
		return this.telemetry.formatPlaybackTimestamp(epochSeconds);
	}

	private cancelAnimation(): void {
		if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
		this.animationFrame = null;
	}
}

const playbackDurationSeconds: Record<string, number> = {
	'10minute': 10 * 60,
	'30minute': 30 * 60,
	'1hour': 60 * 60,
	'2hour': 2 * 60 * 60,
	'3hour': 3 * 60 * 60,
	'6hour': 6 * 60 * 60,
	'12hour': 12 * 60 * 60,
	'1day': 24 * 60 * 60,
};
